'use client';

import type {
  ChatObjectNavigationTarget,
  SubagentOpenTarget,
} from '@terra/ui-design-system';
import { VStack } from '@terra/ui-styled-system';
import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import type { ReactNode } from 'react';
import { CopilotComposerActivityBar } from './CopilotComposerActivityBar';
import { useCopilotRunningAgents } from './useCopilotRunningAgents';
import { useLiveChatTerminals } from './useLiveChatTerminals';

export function useCopilotComposerTopContent({
  chatId,
  messages,
  targetId,
  targetType,
  onOpenActivity,
  onOpenObject,
  interruptedTurnNotice,
  tokenUsagePanel,
  askUserPrompt,
  queuedMessagesPanel,
}: {
  chatId: string;
  messages: TerraUIMessage[];
  targetId?: string;
  targetType?: string;
  onOpenActivity?: (target: SubagentOpenTarget) => void;
  onOpenObject?: (target: ChatObjectNavigationTarget) => void;
  interruptedTurnNotice: ReactNode | undefined;
  tokenUsagePanel: ReactNode | undefined;
  askUserPrompt: ReactNode | undefined;
  queuedMessagesPanel: ReactNode | undefined;
}): ReactNode | undefined {
  const runningAgents = useCopilotRunningAgents({
    chatId,
    messages,
    targetId,
    targetType,
  });
  const terminals = useLiveChatTerminals(chatId);
  const hasRunningAgents =
    runningAgents.isVisible && Boolean(runningAgents.batchId);
  const activityBar =
    hasRunningAgents || terminals.length > 0 ? (
      <CopilotComposerActivityBar
        runningAgents={runningAgents}
        terminals={terminals}
        messages={messages}
        onOpenActivity={onOpenActivity}
        onOpenObject={onOpenObject}
      />
    ) : undefined;
  const composerPrompt = askUserPrompt ?? queuedMessagesPanel;
  const sections = [
    activityBar,
    interruptedTurnNotice,
    tokenUsagePanel,
    composerPrompt,
  ].filter(Boolean);

  if (sections.length === 0) {
    return undefined;
  }

  if (sections.length === 1) {
    return sections[0];
  }

  return (
    <VStack gap={1} alignItems="stretch" width="100%" minW={0}>
      {activityBar}
      {interruptedTurnNotice}
      {tokenUsagePanel}
      {composerPrompt}
    </VStack>
  );
}
